import { queryBuilder } from '@inpart-io/devtools-iap';
import { GET_BY_ID, RETRIEVE_BY_QUERY, useErrorHelper } from '@inpart-io/devtools-vue';
import { type IPrivateFieldCustomValue } from '@inpart-io/padme-iap';
import { privateFieldCustomListValueProperties } from '@inpart-io/padme-iap';
import { computed, type Ref } from 'vue';
import { useStore } from 'vuex';

import { SAVE_SEARCH_RESULT } from '@/store/base/search/actions';
import { GET_SEARCH_RESULT_LIST } from '@/store/base/search/getters';
import { PRIVATE_FIELD_CUSTOM_VALUE_MODULE } from '@/store/private-field-custom-value-module';

export interface IUsePrivateCustomValue {
    customValuesBySearchKey: Ref<IUsePrivateCustomValue[]>;
    getById: (id: number) => IPrivateFieldCustomValue;
    retrieveCustomValue: () => Promise<void>;
    retrieveCustomValueByQuery: (ids: number[]) => Promise<void>;
}

export function usePrivateCustomValue(attr: string, customValue: string, searchKey: string): IUsePrivateCustomValue {
    const store = useStore();
    const errorHelper = useErrorHelper();

    const retrieveCustomValue = async (): Promise<void> => {
        try {
            const ids = await store.dispatch(`${PRIVATE_FIELD_CUSTOM_VALUE_MODULE}/${RETRIEVE_BY_QUERY}`, {
                query: queryBuilder.build(queryBuilder.op.and(queryBuilder.op.and(queryBuilder.cmp.equalTo(attr, customValue))), undefined, undefined, [
                    { direction: 'ASC', attribute: 'position' }
                ]),
                properties: privateFieldCustomListValueProperties.DEFAULT
            });

            void store.dispatch(`${PRIVATE_FIELD_CUSTOM_VALUE_MODULE}/${SAVE_SEARCH_RESULT}`, {
                key: searchKey,
                idList: ids
            });
        } catch (e) {
            errorHelper.handleError(e);
        }
    };

    const retrieveCustomValueByQuery = async (ids: number[]): Promise<void> => {
        try {
            await store.dispatch(`${PRIVATE_FIELD_CUSTOM_VALUE_MODULE}/${RETRIEVE_BY_QUERY}`, {
                query: queryBuilder.build(queryBuilder.op.and(queryBuilder.cmp.equalTo(attr, customValue), queryBuilder.cmp.isIn('dobjId', ids))),
                properties: privateFieldCustomListValueProperties.DEFAULT
            });
        } catch (e) {
            errorHelper.handleError(e);
        }
    };

    const getById = (id: number): IPrivateFieldCustomValue => {
        return store.getters[`${PRIVATE_FIELD_CUSTOM_VALUE_MODULE}/${GET_BY_ID}`](id);
    };

    const customValuesBySearchKey = computed(() => {
        return store.getters[`${PRIVATE_FIELD_CUSTOM_VALUE_MODULE}/${GET_SEARCH_RESULT_LIST}`](searchKey);
    });

    return { getById, customValuesBySearchKey, retrieveCustomValue, retrieveCustomValueByQuery };
}
